import React, { useState } from 'react';
import {
  HomeIcon,
  TruckIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  ComputerDesktopIcon,
  BanknotesIcon,
  CubeIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

const ASSET_TYPE_CONFIG = {
  real_estate: { icon: HomeIcon, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
  vehicle: { icon: TruckIcon, color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
  investment: { icon: ChartBarIcon, color: 'text-purple-600', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
  business: { icon: BuildingOfficeIcon, color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
  electronics: { icon: ComputerDesktopIcon, color: 'text-indigo-600', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' },
  cash: { icon: BanknotesIcon, color: 'text-emerald-600', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
  personal: { icon: CubeIcon, color: 'text-gray-600', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' }
};

const AssetsDesktopCards = ({ 
  assets, 
  liabilityAssets, 
  onEdit, 
  onDelete, 
  expandedRows, 
  onToggleExpanded 
}) => {
  // Safe number conversion helper
  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const formatCurrency = (amount) => {
    const numAmount = toNumber(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateEquity = (currentValue, debt = 0) => {
    const currentVal = toNumber(currentValue);
    const debtAmount = toNumber(debt);
    const result = currentVal - debtAmount;
    return isNaN(result) ? 0 : result;
  };

  const calculateROI = (currentValue, purchasePrice, improvements = 0) => {
    const currentVal = toNumber(currentValue);
    const purchaseVal = toNumber(purchasePrice);
    const improvementVal = toNumber(improvements);
    
    const totalCost = purchaseVal + improvementVal;
    if (totalCost === 0) return 0;
    
    const result = ((currentVal - totalCost) / totalCost) * 100;
    return isNaN(result) ? 0 : result;
  };

  const renderAssetCard = (asset, isLiability = false) => {
    const typeConfig = ASSET_TYPE_CONFIG[asset.type] || ASSET_TYPE_CONFIG.personal;
    const IconComponent = typeConfig.icon;
    const isExpanded = expandedRows.has(asset._id);
    
    const equity = isLiability 
      ? calculateEquity(
          asset.originalAmount || asset.loanDetails?.originalPrincipal, 
          asset.balance || asset.currentBalance
        )
      : calculateEquity(asset.currentValue, asset.loanBalance);
    
    const roi = calculateROI(asset.currentValue, asset.purchasePrice, asset.improvements);

    return (
      <div
        key={asset._id}
        className={`bg-white rounded-xl shadow-sm border-2 ${typeConfig.borderColor} hover:shadow-md transition-all duration-200 overflow-hidden`}
      >
        {/* Card Header */}
        <div className={`${typeConfig.bgColor} px-6 py-4 border-b ${typeConfig.borderColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg bg-white border ${typeConfig.borderColor}`}>
                <IconComponent className={`h-6 w-6 ${typeConfig.color}`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{asset.name}</h3>
                <div className="flex items-center space-x-3 text-sm text-gray-600">
                  <span className="capitalize">{asset.type?.replace('_', ' ')}</span>
                  {asset.condition && (
                    <span className="px-2 py-1 bg-white rounded-full text-xs font-medium">
                      {asset.condition}
                    </span>
                  )}
                  {isLiability && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                      From Loan
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onEdit(asset)}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title={isLiability ? "Edit Asset Details" : "Edit Asset"}
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              {!isLiability && (
                <button
                  onClick={() => onDelete(asset._id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete Asset"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => onToggleExpanded(asset._id)}
                className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                title="View Analysis"
              >
                <EyeIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Card Body - Key Metrics */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Current Value */}
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">
                {formatCurrency(asset.currentValue || asset.originalAmount || asset.loanDetails?.originalPrincipal)}
              </div>
              <div className="text-sm text-gray-500">Current Value</div>
            </div>

            {/* Purchase Price / Original Amount */}
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-700">
                {formatCurrency(asset.purchasePrice || asset.originalAmount || asset.loanDetails?.originalPrincipal)}
              </div>
              <div className="text-sm text-gray-500">
                {isLiability ? 'Original Amount' : 'Purchase Price'}
              </div>
            </div>

            {/* Equity */}
            <div className="text-center">
              <div className={`text-lg font-semibold ${equity >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(equity)}
              </div>
              <div className="text-sm text-gray-500">Equity</div>
            </div>

            {/* ROI or Debt */}
            <div className="text-center">
              {isLiability ? (
                <>
                  <div className="text-lg font-semibold text-red-600">
                    {formatCurrency(asset.balance || asset.currentBalance)}
                  </div>
                  <div className="text-sm text-gray-500">Remaining Debt</div>
                </>
              ) : (
                <>
                  <div className={`text-lg font-semibold ${roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {toNumber(roi).toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500">ROI</div>
                </>
              )}
            </div>
          </div>

          {/* Quick Details Row */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {asset.purchaseDate && (
                <div>
                  <span className="text-gray-500">Purchased:</span>
                  <span className="ml-2 font-medium">{formatDate(asset.purchaseDate)}</span>
                </div>
              )}
              {asset.location && (
                <div>
                  <span className="text-gray-500">Location:</span>
                  <span className="ml-2 font-medium">{asset.location}</span>
                </div>
              )}
              {asset.isShared && (
                <div>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                    Shared Asset
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Expandable Analysis Section */}
        {isExpanded && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900">Asset Analysis</h4>
              <button
                onClick={() => onToggleExpanded(asset._id)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <ChevronUpIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* LEFT COLUMN */}
              <div className="space-y-4">
                {/* Asset Details Section */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-fit">
                  <div className="flex items-center mb-3">
                    <div className="w-5 h-5 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                      <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h5 className="text-sm font-semibold text-gray-900">Asset Details</h5>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium capitalize text-gray-900">{asset.type?.replace('_', ' ') || 'Unknown'}</span>
                    </div>
                    {asset.condition && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Condition:</span>
                        <span className="font-medium capitalize text-gray-900">{asset.condition}</span>
                      </div>
                    )}
                    {isLiability && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Source:</span>
                        <span className="font-medium text-yellow-600">Wallet Loan</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Current Value:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(asset.currentValue || asset.originalAmount || asset.loanDetails?.originalPrincipal)}</span>
                    </div>
                    {(asset.purchasePrice || asset.originalAmount) && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Original Amount:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(asset.purchasePrice || asset.originalAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Equity:</span>
                      <span className={`font-medium ${equity >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(equity)}
                      </span>
                    </div>
                    {isLiability && (asset.balance || asset.currentBalance) && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Remaining Debt:</span>
                        <span className="font-medium text-red-600">{formatCurrency(asset.balance || asset.currentBalance)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Vehicle Details Section */}
                {asset.type === 'vehicle' && (asset.vehicleMake || asset.vehicleModel || asset.vehicleYear || asset.mileage || asset.vinNumber || asset.licensePlate) && (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-fit">
                    <div className="flex items-center mb-3">
                      <div className="w-5 h-5 bg-green-100 rounded-lg flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h5 className="text-sm font-semibold text-gray-900">Vehicle Details</h5>
                    </div>
                    <div className="space-y-2 text-sm">
                      {asset.vehicleMake && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Make:</span>
                          <span className="font-medium text-gray-900">{asset.vehicleMake}</span>
                        </div>
                      )}
                      {asset.vehicleModel && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Model:</span>
                          <span className="font-medium text-gray-900">{asset.vehicleModel}</span>
                        </div>
                      )}
                      {asset.vehicleYear && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Year:</span>
                          <span className="font-medium text-gray-900">{asset.vehicleYear}</span>
                        </div>
                      )}
                      {asset.mileage && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mileage:</span>
                          <span className="font-medium text-gray-900">{parseInt(asset.mileage).toLocaleString()} miles</span>
                        </div>
                      )}
                      {asset.vinNumber && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">VIN:</span>
                          <span className="font-medium text-gray-900 font-mono text-xs">{asset.vinNumber}</span>
                        </div>
                      )}
                      {asset.licensePlate && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">License Plate:</span>
                          <span className="font-medium text-gray-900">{asset.licensePlate}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-4">
                {/* Financial Metrics Section */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-fit">
                  <div className="flex items-center mb-3">
                    <div className="w-5 h-5 bg-emerald-100 rounded-lg flex items-center justify-center mr-2">
                      <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <h5 className="text-sm font-semibold text-gray-900">Financial Metrics</h5>
                  </div>
                <div className="space-y-2 text-sm">
                  {/* Always show basic financial info */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Value:</span>
                    <span className="font-medium text-gray-900">{formatCurrency(asset.currentValue || asset.originalAmount || asset.loanDetails?.originalPrincipal)}</span>
                  </div>
                  {(asset.purchasePrice || asset.originalAmount) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{isLiability ? 'Original Amount:' : 'Purchase Price:'}</span>
                      <span className="font-medium text-gray-900">{formatCurrency(asset.purchasePrice || asset.originalAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Equity/Net Value:</span>
                    <span className={`font-medium ${equity >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(equity)}
                    </span>
                  </div>
                  {!isLiability && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">ROI:</span>
                      <span className={`font-medium ${roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {toNumber(roi).toFixed(1)}%
                      </span>
                    </div>
                  )}
                  {isLiability && (asset.balance || asset.currentBalance) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Remaining Debt:</span>
                      <span className="font-medium text-red-600">{formatCurrency(asset.balance || asset.currentBalance)}</span>
                    </div>
                  )}
                </div>
                </div>

                {/* Financial Information Section */}
                {(asset.financingType || asset.loanBalance || asset.monthlyPayment || asset.interestRate || asset.downPayment || asset.loanTerm || asset.lenderName) && (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-fit">
                    <div className="flex items-center mb-3">
                      <div className="w-5 h-5 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <h5 className="text-sm font-semibold text-gray-900">Financial Information</h5>
                    </div>
                    <div className="space-y-2 text-sm">
                      {asset.financingType && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Financing Type:</span>
                          <span className="font-medium text-gray-900 capitalize">{asset.financingType.replace('_', ' ')}</span>
                        </div>
                      )}
                      {asset.downPayment !== undefined && asset.downPayment !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Down Payment:</span>
                          <span className="font-medium text-gray-900">{formatCurrency(asset.downPayment)}</span>
                        </div>
                      )}
                      {asset.loanBalance !== undefined && asset.loanBalance !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Loan Balance:</span>
                          <span className="font-medium text-red-600">{formatCurrency(asset.loanBalance)}</span>
                        </div>
                      )}
                      {asset.monthlyPayment !== undefined && asset.monthlyPayment !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Monthly Payment:</span>
                          <span className="font-medium text-gray-900">{formatCurrency(asset.monthlyPayment)}</span>
                        </div>
                      )}
                      {asset.interestRate !== undefined && asset.interestRate !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Interest Rate:</span>
                          <span className="font-medium text-gray-900">{asset.interestRate}%</span>
                        </div>
                      )}
                      {asset.loanTerm !== undefined && asset.loanTerm !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Loan Term:</span>
                          <span className="font-medium text-gray-900">{asset.loanTerm} months</span>
                        </div>
                      )}
                      {asset.lenderName && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Lender:</span>
                          <span className="font-medium text-gray-900">{asset.lenderName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Professional & Tax Information Section */}
                {(asset.costBasis || asset.improvements || asset.depreciationMethod || asset.liquidity || asset.insuranceCoverage || asset.taxClassification || asset.appraisalValue || asset.lastAppraisal || asset.incomeGenerated) && (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-fit">
                    <div className="flex items-center mb-3">
                      <div className="w-5 h-5 bg-purple-100 rounded-lg flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h5 className="text-sm font-semibold text-gray-900">Professional & Tax Information</h5>
                    </div>
                    <div className="space-y-2 text-sm">
                      {asset.costBasis !== undefined && asset.costBasis !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Cost Basis:</span>
                          <span className="font-medium text-gray-900">{formatCurrency(asset.costBasis)}</span>
                        </div>
                      )}
                      {asset.improvements !== undefined && asset.improvements !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Improvements:</span>
                          <span className="font-medium text-gray-900">{formatCurrency(asset.improvements)}</span>
                        </div>
                      )}
                      {asset.depreciationMethod && asset.depreciationMethod !== 'none' && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Depreciation Method:</span>
                          <span className="font-medium text-gray-900 capitalize">{asset.depreciationMethod.replace('-', ' ')}</span>
                        </div>
                      )}
                      {asset.liquidity && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Liquidity:</span>
                          <span className={`font-medium capitalize ${
                            asset.liquidity === 'high' ? 'text-green-600' : 
                            asset.liquidity === 'medium' ? 'text-yellow-600' : 'text-red-600'
                          }`}>{asset.liquidity}</span>
                        </div>
                      )}
                      {asset.insuranceCoverage !== undefined && asset.insuranceCoverage !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Insurance Coverage:</span>
                          <span className="font-medium text-gray-900">{formatCurrency(asset.insuranceCoverage)}</span>
                        </div>
                      )}
                      {asset.taxClassification && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tax Classification:</span>
                          <span className="font-medium text-gray-900 capitalize">{asset.taxClassification}</span>
                        </div>
                      )}
                      {asset.incomeGenerated !== undefined && asset.incomeGenerated !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Monthly Income:</span>
                          <span className="font-medium text-green-600">{formatCurrency(asset.incomeGenerated)}</span>
                        </div>
                      )}
                      {asset.appraisalValue !== undefined && asset.appraisalValue !== null && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Appraisal Value:</span>
                          <span className="font-medium text-green-600">{formatCurrency(asset.appraisalValue)}</span>
                        </div>
                      )}
                      {asset.lastAppraisal && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Last Appraisal:</span>
                          <span className="font-medium text-gray-900">{formatDate(asset.lastAppraisal)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>


            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Manual Assets */}
      {assets.length > 0 && (
        <div className="space-y-4">
          {assets.map(asset => renderAssetCard(asset, false))}
        </div>
      )}

      {/* Liability Assets */}
      {liabilityAssets.length > 0 && (
        <div className="space-y-4">
          {liabilityAssets.map(asset => renderAssetCard(asset, true))}
        </div>
      )}

      {/* Empty State */}
      {assets.length === 0 && liabilityAssets.length === 0 && (
        <div className="text-center py-12">
          <CubeIcon className="h-12 w-12 text-gray-400 mb-4 mx-auto" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No assets yet</h3>
          <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
            Start tracking your valuable assets to get a complete picture of your net worth.
            Assets from loan accounts will automatically appear here when you add loan details.
          </p>
        </div>
      )}
    </div>
  );
};

export default AssetsDesktopCards; 